import type { BookingConfirmationSummary, InsuranceOption } from '@massilia/queries'

import {
  useEstimateMyBookingInsuranceQuery,
  useInitiateInsurancePaymentMutation,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { AlertTriangle, ExternalLink, Loader2, Shield } from 'lucide-react'

import { formatPrice } from '@/utils/format-price'

interface InsuranceUpsellPanelProps {
  bookingId?: string
  sessionId?: string
  summary?: BookingConfirmationSummary
}

export function InsuranceUpsellPanel({ bookingId, sessionId, summary }: InsuranceUpsellPanelProps) {
  const summaryEstimate = summary?.insuranceEstimate
  const bookingEstimate = useEstimateMyBookingInsuranceQuery(
    bookingId,
    !!bookingId && !summaryEstimate
  )

  const paymentMutation = useInitiateInsurancePaymentMutation()

  const isLoading = !summaryEstimate ? bookingEstimate.isLoading : false
  const isError = !summaryEstimate ? bookingEstimate.isError : false
  const refetch = bookingEstimate.refetch

  // Three-state semantics for `option`:
  // - undefined: still loading / not yet resolved
  // - null: explicitly unavailable (no insurance option for this booking)
  // - InsuranceOption: valid option ready to display
  let option: InsuranceOption | null | undefined
  if (summaryEstimate) {
    option = summaryEstimate.option
  } else if (bookingEstimate.data) {
    option = bookingEstimate.data.selectedOption
  }

  const resolvedBookingId = summary?.bookingId ?? bookingId

  const handlePay = () => {
    if (!resolvedBookingId) return
    paymentMutation.mutate(
      { bookingId: resolvedBookingId, sessionId },
      {
        onSuccess: (data) => {
          window.location.assign(data.axeptaRedirectUrl)
        },
      }
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="text-brand-600 size-6 animate-spin" />
        <span className="text-gray-light-600 ml-2 text-sm">Estimation en cours...</span>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="size-4" />
          <span>Erreur lors de l&apos;estimation de l&apos;assurance</span>
        </div>
        <Button onClick={() => refetch()} size="sm" variant="outline">
          Réessayer
        </Button>
      </div>
    )
  }

  // No option available
  if (option === null) {
    return (
      <div className="py-4 text-center">
        <p className="text-gray-light-600 text-sm">Aucune assurance disponible pour ce voyage.</p>
      </div>
    )
  }

  // Show estimate details
  if (option) {
    const priceLabel = formatPrice(option.prixVente)

    return (
      <div className="flex flex-col gap-4 pt-2">
        <div className="border-gray-light-200 flex flex-col gap-4 rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="bg-brand-50 flex size-9 items-center justify-center rounded-lg">
              <Shield className="text-brand-600 size-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-light-900 text-sm font-semibold">
                {option.nomAssurance}
              </span>
              <span className="text-gray-light-600 text-xs font-medium">{option.nomFormule}</span>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-light-700 font-medium">Prix</span>
            <span className="text-gray-light-900 font-semibold">{priceLabel}</span>
          </div>

          {option.dinLink && (
            <div className="flex flex-wrap gap-3">
              <a
                className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-xs font-medium"
                href={option.dinLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Documents d&apos;informations <ExternalLink className="size-3" />
              </a>
            </div>
          )}
        </div>

        <Button
          className="w-full gap-2"
          disabled={paymentMutation.isPending || !resolvedBookingId}
          onClick={handlePay}
        >
          {paymentMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Shield className="size-4" />
          )}
          Payer l&apos;assurance - {priceLabel}
        </Button>

        {paymentMutation.isError && (
          <p className="text-center text-sm text-red-600">
            Erreur lors de l&apos;initiation du paiement. Veuillez réessayer.
          </p>
        )}
      </div>
    )
  }

  return null
}
